/**
 * Classeur des 7 familles pédagogiques — CCS 2 Roues
 * ============================================================
 *
 * Toutes les études de cas tirées au sort le jour de l'examen CCS 2R se
 * rattachent à l'une des 7 familles pédagogiques. Le classeur permet de
 * structurer une action de formation en salle sans avoir à mémoriser 27
 * scénarios différents.
 *
 * Source : « CCS2R · Classeur 7 Familles · Final » (PDF fourni par l'auteur)
 *
 * Chronométrage de l'épreuve :
 *  - 20 min de préparation
 *  - 20 min de présentation au tableau blanc
 *  - 20 min d'échange avec le jury
 */

// ─── Méta du tableau de construction (identique pour toutes les familles) ────
export const PHASES_CONSTRUCTION = [
  {
    phase: 'Présentation',
    fait: 'Présentation formateur · créer un climat favorable',
    objectif: 'Écoute',
    activite: 'Écoute',
    support: 'Expositive',
    justification: 'Mettre en confiance',
  },
  {
    phase: 'Tour de table',
    fait: 'Questionnement de l\'expérience',
    objectif: 'Identifier les acquis',
    activite: 'Échange',
    support: 'Interrogative',
    justification: 'Adapter la séance',
  },
  {
    phase: 'Présentation séance',
    fait: 'Annonce des objectifs',
    objectif: 'Donner du sens',
    activite: 'Écoute',
    support: 'Expositive',
    justification: "Favoriser l'adhésion",
  },
  {
    phase: 'Diagnostic',
    fait: 'Quiz / photo / débat',
    objectif: 'Repérer les acquis',
    activite: 'Répond',
    support: 'Active',
    justification: 'Adapter les contenus',
  },
  {
    phase: 'Objectif 1',
    fait: 'Apport de connaissances',
    objectif: 'Connaître',
    activite: 'Observe',
    support: 'Support adapté',
    justification: 'Acquérir les bases',
  },
  {
    phase: 'Objectif 2',
    fait: 'Analyse de situations',
    objectif: 'Comprendre',
    activite: 'Analyse',
    support: 'Étude de cas',
    justification: 'Donner du sens',
  },
  {
    phase: 'Objectif 3',
    fait: 'Recherche de solutions',
    objectif: 'Agir',
    activite: 'Propose',
    support: 'Travail collectif',
    justification: "Favoriser l'implication",
  },
  {
    phase: 'Évaluation',
    fait: 'Cas pratique / QCM',
    objectif: 'Vérifier les acquis',
    activite: 'Répond',
    support: 'Évaluation',
    justification: 'Mesurer les objectifs',
  },
  {
    phase: 'Bilan',
    fait: 'Synthèse',
    objectif: 'Consolider',
    activite: 'Reformule',
    support: 'Échange',
    justification: 'Ancrer les messages',
  },
  {
    phase: 'Conclusion',
    fait: 'Ouverture pratique',
    objectif: 'Transfert',
    activite: 'Écoute',
    support: 'Synthèse',
    justification: 'Lien avec la conduite',
  },
];

// ─── Leviers / freins communs aux 7 familles ─────────────────────────────────
export const COMMON_LEVIERS = ['Motivation', 'Expérience', 'Curiosité', 'Projet', 'Autonomie'];
export const COMMON_FREINS = [
  'Stress',
  'Sur-confiance',
  'Manque d\'expérience',
  'Représentations erronées',
];
export const COMMON_SUPPORTS = [
  'Écoute',
  'Questionnement',
  'Quiz / photo / débat',
  'Support adapté',
  'Étude de cas',
  'Travail collectif',
];

// ─── Les 7 familles ──────────────────────────────────────────────────────────
export const FAMILIES = [
  {
    slug: 'comportement',
    title: 'Comportement',
    accent: '#cf2031',
    cas: [1, 4, 26],
    aspect: 'Faire évoluer le comportement',
    objectifGeneral: 'Amener le public à modifier durablement un comportement à risque par la prise de conscience et le transfert.',
    publicVise: 'Élèves dont le comportement de conduite doit évoluer (nouveaux motards, post-permis, élèves à risque).',
    motsClesJury: ['représentations', 'prise de conscience', 'comportement à risque', 'transfert'],
  },
  {
    slug: 'risque-statistique',
    title: 'Facteur de risque statistique',
    accent: '#dc2626',
    cas: [2, 10, 11, 14, 15, 17],
    aspect: 'Faire prendre conscience du risque réel',
    objectifGeneral: 'Amener le public à objectiver le risque grâce aux données factuelles (ONISR, accidentalité).',
    publicVise: 'Élèves qui sous-estiment leur exposition réelle au risque (jeunes motards, conducteurs expérimentés en plaisir).',
    motsClesJury: ['accidentalité', 'exposition au risque', 'ONISR', 'gravité'],
  },
  {
    slug: 'reglementation',
    title: 'Réglementation & signalisation',
    accent: '#0f766e',
    cas: [3, 8, 9],
    aspect: 'Rendre compréhensible le sens de la règle',
    objectifGeneral: "Donner du sens à la règle pour permettre une application volontaire et éclairée — pas une obéissance mécanique.",
    publicVise: 'Élèves qui appliquent la règle sans la comprendre, ou qui la contestent par incompréhension.',
    motsClesJury: ['règle', 'compréhension', 'application', 'sécurité'],
  },
  {
    slug: 'vehicule',
    title: 'Connaissance du véhicule',
    accent: '#f59e0b',
    cas: [5, 7, 12, 18, 19, 21],
    aspect: 'Rendre compréhensible le fonctionnement du véhicule',
    objectifGeneral: 'Aider le public à entretenir, contrôler et utiliser sa machine en sécurité.',
    publicVise: 'Élèves qui ne maîtrisent pas les vérifications ni les organes essentiels (freinage, pneumatiques, transmission).',
    motsClesJury: ['entretien', 'contrôle', 'conformité', 'équipement'],
  },
  {
    slug: 'physiologique',
    title: 'Physiologique & psychologique',
    accent: '#7c3aed',
    cas: [6, 22, 23],
    aspect: 'Faire prendre conscience des limites humaines',
    objectifGeneral: 'Faire reconnaître les seuils physiologiques (fatigue, alcool, perception) et leur impact sur la sécurité.',
    publicVise: 'Élèves confrontés à la fatigue, l\'alcool, le stress ou les altérations de la perception.',
    motsClesJury: ['vigilance', 'fatigue', 'alcool', 'perception'],
  },
  {
    slug: 'physique',
    title: 'Aspect physique',
    accent: '#0891b2',
    cas: [24, 25, 27],
    aspect: 'Rendre simple une notion physique complexe',
    objectifGeneral: 'Vulgariser les lois physiques (adhérence, trajectoire, freinage) pour donner aux élèves des repères concrets de conduite.',
    publicVise: 'Élèves devant intégrer adhérence, trajectoires de sécurité, transferts de masse, distance de freinage.',
    motsClesJury: ['adhérence', 'trajectoire', 'transfert de masse', 'freinage'],
  },
  {
    slug: 'conscientisation',
    title: 'Conscientisation & approche sociale',
    accent: '#ea580c',
    cas: [13, 16, 20],
    aspect: 'Faire évoluer les représentations',
    objectifGeneral: 'Travailler sur les influences sociales et les fausses représentations pour faire évoluer durablement les attitudes.',
    publicVise: 'Élèves dont les représentations (pairs, réseaux, culture moto) brouillent l\'appréciation du risque.',
    motsClesJury: ['influence sociale', 'perception', 'surestimation', 'prise de conscience'],
  },
];

// ─── Récupération d'une famille à partir d'un numéro de cas ──────────────────
export function getFamilyByCas(num) {
  return FAMILIES.find((f) => f.cas.includes(num)) || null;
}

export function getFamilyBySlug(slug) {
  return FAMILIES.find((f) => f.slug === slug) || null;
}

// ─── Fiche réaction rapide examen ────────────────────────────────────────────
export const REACTION_RAPIDE = [
  { step: 1, label: 'Identifier la famille', hint: 'À partir du numéro du cas tiré au sort.' },
  { step: 2, label: 'Identifier le public', hint: 'Qui est concerné, quel est le profil ?' },
  { step: 3, label: 'Identifier les leviers', hint: 'Sur quoi je peux m\'appuyer pour faire passer le message.' },
  { step: 4, label: 'Identifier les freins', hint: 'Ce qui peut bloquer (stress, sur-confiance, représentations).' },
  { step: 5, label: 'Construire : Connaître / Comprendre / Agir', hint: 'Les 3 objectifs intermédiaires de la séance.' },
  { step: 6, label: 'Choisir les supports', hint: 'Quiz, photo, étude de cas, débat, travail collectif…' },
  { step: 7, label: "Préparer l'évaluation", hint: 'Comment je vérifie que les objectifs sont atteints.' },
  { step: 8, label: 'Préparer les réponses au jury', hint: 'Justifier méthode, support, leviers, freins, évaluation.' },
];

// ─── Phases chronométrées de l'épreuve CCS 2R ───────────────────────────────
export const EPREUVE_PHASES = [
  {
    id: 'preparation',
    label: 'Préparation',
    durationSec: 20 * 60,
    color: '#7c3aed',
    description: "Vous tirez un cas au sort. 20 minutes pour analyser, identifier la famille, structurer votre action et préparer vos arguments jury.",
    checklist: [
      'Lire le cas attentivement',
      'Identifier la famille pédagogique',
      'Identifier le public visé',
      'Lister leviers et freins',
      'Choisir 3 objectifs (Connaître / Comprendre / Agir)',
      'Sélectionner les supports pédagogiques',
      'Préparer l\'évaluation finale',
      'Anticiper 3-4 questions du jury',
    ],
  },
  {
    id: 'presentation',
    label: 'Présentation au tableau',
    durationSec: 20 * 60,
    color: '#cf2031',
    description: "20 minutes pour présenter votre action devant le jury, au tableau blanc, en respectant les 10 phases de la structure.",
    checklist: [
      'Présentation formateur (1 min)',
      'Tour de table — recueil expérience (2 min)',
      'Annonce des objectifs de la séance (1 min)',
      'Évaluation diagnostique (2 min)',
      'Objectif 1 — Connaître (4 min)',
      'Objectif 2 — Comprendre (4 min)',
      'Objectif 3 — Agir (3 min)',
      'Évaluation + bilan + conclusion (3 min)',
    ],
  },
  {
    id: 'jury',
    label: 'Échange avec le jury',
    durationSec: 20 * 60,
    color: '#0891b2',
    description: "20 minutes de questions du jury sur vos choix pédagogiques. Justifier méthode, support, leviers, freins, évaluation.",
    checklist: [
      'Pourquoi cette méthode ?',
      'Pourquoi ce support ?',
      'Quels leviers ?',
      'Quels freins ?',
      'Comment évaluez-vous ?',
      'Quelle posture face à un élève en difficulté ?',
      'Quel transfert vers la conduite réelle ?',
      'Lien avec la matrice GDE / REMC',
    ],
  },
];
